import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Container, Link, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import './App.css';
import Footer from './Footer';
import LawAppBar from './LawAppBar';
import { getPressReleaseBySlug } from './press/index';
import './PressRelease.css';

export default function PressRelease() {
    const [cookies] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);
    const navigate = useNavigate();
    const { slug } = useParams();
    const [pressRelease, setPressRelease] = useState(null);
    const [PressReleaseComponent, setPressReleaseComponent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPressRelease = async () => {
            const found = getPressReleaseBySlug(slug);
            if (!found) {
                setLoading(false);
                return;
            }

            setPressRelease(found);

            // Set document title for SEO
            document.title = `${found.title} | AttorneyAide Press`;

            // Set meta description for SEO
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.setAttribute('content', found.summary);
            } else {
                const meta = document.createElement('meta');
                meta.name = 'description';
                meta.content = found.summary;
                document.getElementsByTagName('head')[0].appendChild(meta);
            }

            // Dynamically load the press release component
            try {
                const module = await found.component();
                setPressReleaseComponent(() => module.default);
            } catch (error) {
                console.error('Error loading press release component:', error);
            } finally {
                setLoading(false);
            }
        };

        loadPressRelease();

        // Cleanup: reset title when component unmounts
        return () => {
            document.title = 'AttorneyAide';
        };
    }, [slug]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="App">
                <LawAppBar cookies={cookies} />
                <Container
                    maxWidth="lg"
                    sx={{
                        padding: { xs: '20px', sm: '40px' },
                        marginTop: '40px',
                        marginBottom: '60px',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
                        Loading...
                    </Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    if (!pressRelease || !PressReleaseComponent) {
        return (
            <div className="App">
                <LawAppBar cookies={cookies} />
                <Container
                    maxWidth="lg"
                    sx={{
                        padding: { xs: '20px', sm: '40px' },
                        marginTop: '40px',
                        marginBottom: '60px',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" sx={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                        Press Release Not Found
                    </Typography>
                    <Link
                        onClick={() => navigate('/press')}
                        sx={{
                            color: 'var(--accent-color)',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        Return to Press Releases
                    </Link>
                </Container>
                <Footer />
            </div>
        );
    }

    return (
        <div className="App">
            <LawAppBar cookies={cookies} />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px',
                    textAlign: 'left'
                }}
            >
                {/* Back Navigation */}
                <Box sx={{ marginBottom: '24px' }}>
                    <Link
                        onClick={() => navigate('/press')}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontSize: '0.95rem',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                                color: 'var(--text-primary)'
                            }
                        }}
                    >
                        <ArrowBackIcon fontSize="small" />
                        Back to All Articles
                    </Link>
                </Box>

                {/* Article Container */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        textAlign: 'left'
                    }}
                >
                    {/* Article Header */}
                    <Box sx={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid var(--border-color)' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'var(--text-secondary)',
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                fontWeight: 500,
                                marginBottom: '16px'
                            }}
                        >
                            {formatDate(pressRelease.date)}
                        </Typography>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 400,
                                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                                lineHeight: 1.3,
                                letterSpacing: '-0.01em'
                            }}
                        >
                            {pressRelease.title}
                        </Typography>
                    </Box>

                    {/* Article Content */}
                    <Box component="article">
                        <PressReleaseComponent />
                    </Box>
                </Paper>
            </Container>
            <Footer />
        </div>
    );
}

